/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useEffect, useMemo, useRef, useState } from 'react';

// eslint-disable-next-line @coze-arch/no-batch-import-or-export
import * as echarts from 'echarts';
import dayjs from 'dayjs';
import { I18n } from '@coze-arch/i18n';
import {
  Button,
  Input,
  Select,
  Space,
  type ColumnProps,
} from '@coze-arch/coze-design';
import {
  type UsageRecordItem,
  type UsageAggregateItem,
} from '@coze-arch/bot-api/usage_permission_api';

import { AuthTable } from '@/components/auth-table';
import {
  useGetUsageAggregate,
  useGetUsageList,
  useGetUsageFacet,
  type UsageQuery,
} from '@/hooks/usage/use-usage';

const PAGE_SIZE = 20;

const sourceName = (v: number) =>
  v === 2 ? I18n.t('usage_source_oauth') : I18n.t('usage_source_pat');

const bizTypeName = (v: number) =>
  ({
    1: I18n.t('usage_biz_chat'),
    2: I18n.t('usage_biz_workflow'),
    3: I18n.t('usage_biz_knowledge'),
  })[v] ?? '-';

interface DraftFilters {
  range: string;
  source: number;
  credential_id: number;
  agent_id: number;
  model_name: string;
  end_user_id: string;
  biz_type: number;
}

const resolveRange = (range: string): { start?: number; end?: number } => {
  const now = dayjs();
  switch (range) {
    case 'today':
      return { start: now.startOf('day').unix(), end: now.unix() };
    case '7d':
      return { start: now.subtract(7, 'day').unix(), end: now.unix() };
    case '30d':
      return { start: now.subtract(30, 'day').unix(), end: now.unix() };
    default:
      return {};
  }
};

// eslint-disable-next-line @coze-arch/max-line-per-function
export const UsageBody: React.FC<{ spaceId: string }> = ({ spaceId }) => {
  const [draft, setDraft] = useState<DraftFilters>({
    range: '7d',
    source: 0,
    credential_id: 0,
    agent_id: 0,
    model_name: '',
    end_user_id: '',
    biz_type: 0,
  });
  const [filters, setFilters] = useState<UsageQuery>({});
  const [page, setPage] = useState(1);
  const [groupBy, setGroupBy] = useState('model');
  const [trendGranularity, setTrendGranularity] = useState<'day' | 'hour'>('day');

  const facet = useGetUsageFacet({ spaceId });
  const summaryAgg = useGetUsageAggregate({ spaceId, query: filters, groupBy: 'model' });
  const trendAgg = useGetUsageAggregate({ spaceId, query: filters, groupBy: trendGranularity });
  const groupAgg = useGetUsageAggregate({ spaceId, query: filters, groupBy });
  const list = useGetUsageList({ spaceId, query: filters, page, size: PAGE_SIZE });

  const trendRef = useRef<HTMLDivElement>(null);

  const rangeOptions = [
    { value: 'all', label: I18n.t('usage_range_all') },
    { value: 'today', label: I18n.t('usage_range_today') },
    { value: '7d', label: I18n.t('usage_range_7d') },
    { value: '30d', label: I18n.t('usage_range_30d') },
  ];
  const sourceOptions = [
    { value: 0, label: I18n.t('usage_range_all') },
    { value: 1, label: I18n.t('usage_source_pat') },
    { value: 2, label: I18n.t('usage_source_oauth') },
  ];
  const bizTypeOptions = [
    { value: 0, label: I18n.t('usage_range_all') },
    { value: 1, label: I18n.t('usage_biz_chat') },
    { value: 2, label: I18n.t('usage_biz_workflow') },
    { value: 3, label: I18n.t('usage_biz_knowledge') },
  ];
  const groupOptions = [
    { value: 'model', label: I18n.t('usage_dim_model') },
    { value: 'oauth_app', label: I18n.t('usage_dim_oauth') },
    { value: 'end_user', label: I18n.t('usage_dim_end_user') },
    { value: 'agent', label: I18n.t('usage_dim_agent') },
  ];
  const modelOptions = [
    { value: '', label: I18n.t('usage_range_all') },
    ...facet.models.map(m => ({ value: m.value, label: m.label })),
  ];
  const oauthOptions = [
    { value: 0, label: I18n.t('usage_range_all') },
    ...facet.oauthApps.map(a => ({ value: Number(a.value), label: a.label })),
  ];
  const agentOptions = [
    { value: 0, label: I18n.t('usage_range_all') },
    ...facet.agents.map(a => ({ value: Number(a.value), label: a.label })),
  ];

  const summary = useMemo(() => {
    let calls = 0;
    let input = 0;
    let output = 0;
    for (const it of summaryAgg.items) {
      calls += Number(it.calls ?? 0);
      input += Number(it.prompt_tokens ?? 0);
      output += Number(it.completion_tokens ?? 0);
    }
    return { calls, input, output, total: Number(summaryAgg.totalTokens ?? 0) };
  }, [summaryAgg]);

  useEffect(() => {
    if (!trendRef.current || trendAgg.items.length === 0) {return;}
    const chart = echarts.init(trendRef.current);
    const buckets = trendAgg.items.map(i => i.time_bucket ?? '');
    chart.setOption({
      tooltip: { trigger: 'axis' },
      legend: { data: ['Tokens', I18n.t('usage_calls')] },
      xAxis: { type: 'category', data: buckets },
      yAxis: [
        { type: 'value', name: 'Tokens' },
        { type: 'value', name: I18n.t('usage_calls') },
      ],
      series: [
        {
          name: 'Tokens',
          type: 'line',
          data: trendAgg.items.map(i => Number(i.total_tokens)),
        },
        {
          name: I18n.t('usage_calls'),
          type: 'bar',
          yAxisIndex: 1,
          data: trendAgg.items.map(i => Number(i.calls)),
        },
      ],
    });
    return () => chart.dispose();
  }, [trendAgg]);

  const handleSearch = () => {
    const range = resolveRange(draft.range);
    setPage(1);
    setFilters({
      source: draft.source || undefined,
      credential_id: draft.credential_id || undefined,
      agent_id: draft.agent_id || undefined,
      model_name: draft.model_name || undefined,
      end_user_id: draft.end_user_id || undefined,
      biz_type: draft.biz_type || undefined,
      start: range.start,
      end: range.end,
    });
  };

  const handleReset = () => {
    setDraft({
      range: '7d',
      source: 0,
      credential_id: 0,
      agent_id: 0,
      model_name: '',
      end_user_id: '',
      biz_type: 0,
    });
    setFilters({});
    setPage(1);
  };

  const handleExport = () => {
    const params = new URLSearchParams();
    params.set('space_id', spaceId);
    if (filters.source !== undefined) {params.set('source', String(filters.source));}
    if (filters.credential_id !== undefined) {params.set('credential_id', String(filters.credential_id));}
    if (filters.agent_id !== undefined) {params.set('agent_id', String(filters.agent_id));}
    if (filters.model_name) {params.set('model_name', filters.model_name);}
    if (filters.end_user_id) {params.set('end_user_id', filters.end_user_id);}
    if (filters.biz_type !== undefined) {params.set('biz_type', String(filters.biz_type));}
    if (filters.start !== undefined) {params.set('start', String(filters.start));}
    if (filters.end !== undefined) {params.set('end', String(filters.end));}
    window.open(`/api/permission_api/usage/export?${params.toString()}`, '_blank');
  };

  const groupColumns: ColumnProps<UsageAggregateItem>[] = [
    {
      title: I18n.t('usage_group_label'),
      dataIndex: 'label',
      key: 'label',
      render: (_, record) => {
        if (groupBy === 'oauth_app') {return record.credential_name || String(record.credential_id ?? '-');}
        if (groupBy === 'end_user') {return record.end_user_id || '-';}
        if (groupBy === 'agent') {return record.agent_name || String(record.agent_id ?? '-');}
        return record.model_name || '-';
      },
    },
    { title: I18n.t('usage_calls'), dataIndex: 'calls', key: 'calls' },
    { title: I18n.t('usage_input_tokens'), dataIndex: 'prompt_tokens', key: 'prompt_tokens' },
    { title: I18n.t('usage_output_tokens'), dataIndex: 'completion_tokens', key: 'completion_tokens' },
    { title: I18n.t('usage_total_tokens_col'), dataIndex: 'total_tokens', key: 'total_tokens' },
  ];

  const detailColumns: ColumnProps<UsageRecordItem>[] = [
    { title: I18n.t('usage_model'), dataIndex: 'model_name', key: 'model_name' },
    {
      title: I18n.t('usage_source'),
      dataIndex: 'source',
      key: 'source',
      render: (_, record) => sourceName(record.source),
    },
    {
      title: I18n.t('usage_agent'),
      dataIndex: 'agent_id',
      key: 'agent_id',
      render: (_, record) => record.agent_name || String(record.agent_id ?? '-'),
    },
    { title: I18n.t('usage_end_user'), dataIndex: 'end_user_id', key: 'end_user_id' },
    { title: I18n.t('usage_input_tokens'), dataIndex: 'prompt_tokens', key: 'prompt_tokens' },
    { title: I18n.t('usage_output_tokens'), dataIndex: 'completion_tokens', key: 'completion_tokens' },
    { title: I18n.t('usage_total_tokens_col'), dataIndex: 'total_tokens', key: 'total_tokens' },
    {
      title: I18n.t('usage_biz_type'),
      dataIndex: 'biz_type',
      key: 'biz_type',
      render: (_, record) => bizTypeName(record.biz_type),
    },
    {
      title: I18n.t('usage_time'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (_, record) => dayjs.unix(Number(record.created_at)).format('YYYY-MM-DD HH:mm:ss'),
    },
  ];

  return (
    <div className="w-full h-full flex flex-col gap-4">
      <Space wrap className="w-full items-end">
        <div>
          <div className="text-xs mb-1 text-gray-500">{I18n.t('usage_time_range')}</div>
          <Select
            style={{ width: 120 }}
            value={draft.range}
            optionList={rangeOptions}
            onChange={v => setDraft({ ...draft, range: String(v) })}
          />
        </div>
        <div>
          <div className="text-xs mb-1 text-gray-500">{I18n.t('usage_source')}</div>
          <Select
            style={{ width: 120 }}
            value={draft.source}
            optionList={sourceOptions}
            onChange={v => setDraft({ ...draft, source: v as number })}
          />
        </div>
        <div>
          <div className="text-xs mb-1 text-gray-500">{I18n.t('oauth_app')}</div>
          <Select
            style={{ width: 180 }}
            value={draft.credential_id}
            optionList={oauthOptions}
            onChange={v => setDraft({ ...draft, credential_id: v as number })}
          />
        </div>
        <div>
          <div className="text-xs mb-1 text-gray-500">{I18n.t('usage_agent')}</div>
          <Select
            style={{ width: 160 }}
            value={draft.agent_id}
            optionList={agentOptions}
            onChange={v => setDraft({ ...draft, agent_id: v as number })}
          />
        </div>
        <div>
          <div className="text-xs mb-1 text-gray-500">{I18n.t('usage_model')}</div>
          <Select
            style={{ width: 160 }}
            value={draft.model_name}
            optionList={modelOptions}
            onChange={v => setDraft({ ...draft, model_name: String(v ?? '') })}
          />
        </div>
        <div>
          <div className="text-xs mb-1 text-gray-500">{I18n.t('usage_end_user')}</div>
          <Input
            style={{ width: 160 }}
            value={draft.end_user_id}
            placeholder={I18n.t('usage_end_user')}
            onChange={v => setDraft({ ...draft, end_user_id: v })}
          />
        </div>
        <div>
          <div className="text-xs mb-1 text-gray-500">{I18n.t('usage_biz_type')}</div>
          <Select
            style={{ width: 120 }}
            value={draft.biz_type}
            optionList={bizTypeOptions}
            onChange={v => setDraft({ ...draft, biz_type: v as number })}
          />
        </div>
        <Space>
          <Button type="primary" theme="solid" onClick={handleSearch}>
            {I18n.t('usage_query')}
          </Button>
          <Button onClick={handleReset}>{I18n.t('usage_reset')}</Button>
        </Space>
      </Space>

      <div className="grid grid-cols-5 gap-4">
        <div className="rounded-lg border border-gray-100 p-4">
          <div className="text-sm text-gray-500">{I18n.t('usage_total_tokens_col')}</div>
          <div className="text-2xl font-semibold mt-1">{summary.total}</div>
        </div>
        <div className="rounded-lg border border-gray-100 p-4">
          <div className="text-sm text-gray-500">{I18n.t('usage_calls')}</div>
          <div className="text-2xl font-semibold mt-1">{summary.calls}</div>
        </div>
        <div className="rounded-lg border border-gray-100 p-4">
          <div className="text-sm text-gray-500">{I18n.t('usage_input_tokens')}</div>
          <div className="text-2xl font-semibold mt-1">{summary.input}</div>
        </div>
        <div className="rounded-lg border border-gray-100 p-4">
          <div className="text-sm text-gray-500">{I18n.t('usage_output_tokens')}</div>
          <div className="text-2xl font-semibold mt-1">{summary.output}</div>
        </div>
        <div className="rounded-lg border border-gray-100 p-4">
          <div className="text-sm text-gray-500">{I18n.t('usage_distinct_end_users')}</div>
          <div className="text-2xl font-semibold mt-1">{facet.endUsers.length}</div>
        </div>
      </div>

      <Space className="w-full justify-between">
        <span className="text-sm text-gray-500">{I18n.t('usage_trend')}</span>
        <Select
          style={{ width: 120 }}
          value={trendGranularity}
          optionList={[
            { value: 'day', label: I18n.t('usage_granularity_day') },
            { value: 'hour', label: I18n.t('usage_granularity_hour') },
          ]}
          onChange={v => setTrendGranularity(v as 'day' | 'hour')}
        />
      </Space>
      <div ref={trendRef} style={{ width: '100%', height: 260 }} />

      <Space className="w-full justify-between">
        <Space>
          <span className="text-sm text-gray-500">{I18n.t('usage_dimension')}</span>
          <Select
            style={{ width: 140 }}
            value={groupBy}
            optionList={groupOptions}
            onChange={v => setGroupBy(String(v))}
          />
        </Space>
      </Space>
      <AuthTable
        tableProps={{
          rowKey: (record: UsageAggregateItem) =>
            `${record.model_name}-${record.credential_id}-${record.end_user_id}-${record.time_bucket}`,
          loading: groupAgg.loading,
          dataSource: groupAgg.items,
          columns: groupColumns,
        }}
      />

      <Space className="w-full justify-between">
        <h4 className="m-0">{I18n.t('usage_detail')}</h4>
        <Button onClick={handleExport}>{I18n.t('usage_export')}</Button>
      </Space>
      <AuthTable
        tableProps={{
          rowKey: 'id',
          loading: list.loading,
          dataSource: list.items,
          columns: detailColumns,
        }}
      />
      <Space className="justify-end">
        <Button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>
          {I18n.t('usage_prev')}
        </Button>
        <span>{page}</span>
        <Button disabled={!list.hasMore} onClick={() => setPage(p => p + 1)}>
          {I18n.t('usage_next')}
        </Button>
      </Space>
    </div>
  );
};
